<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { AlertTriangle, Copy, RefreshCw, Home, MessageSquarePlus, Check } from 'lucide-vue-next'
import BaseModal from '@/core/components/BaseModal.vue'

interface ErrorState {
  visible: boolean
  errorRef: string | null
  message: string
}

const props = defineProps<{
  error: ErrorState
}>()

const emit = defineEmits<{
  close: []
  reportProblem: [errorRef: string | null]
}>()

const router = useRouter()
const copied = ref(false)

async function copyErrorRef() {
  if (!props.error.errorRef) return
  try {
    await navigator.clipboard.writeText(props.error.errorRef)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch {
    // Fallback for older browsers
    const textarea = document.createElement('textarea')
    textarea.value = props.error.errorRef
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  }
}

function retry() {
  emit('close')
  window.location.reload()
}

function goHome() {
  emit('close')
  router.push('/')
}

function reportProblem() {
  emit('reportProblem', props.error.errorRef)
  emit('close')
}
</script>

<template>
  <BaseModal
    :model-value="error.visible"
    size="sm"
    :closable="false"
    @update:model-value="$emit('close')"
  >
    <div class="space-y-4">
      <!-- Icon -->
      <div class="flex justify-center">
        <div class="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
          <AlertTriangle class="w-8 h-8 text-red-600 dark:text-red-400" />
        </div>
      </div>

      <!-- Title -->
      <h2 class="text-xl font-bold text-center text-slate-900 dark:text-white">
        Si è verificato un errore
      </h2>

      <!-- Message -->
      <p class="text-center text-slate-600 dark:text-slate-400 text-sm">
        {{ error.message || 'Si è verificato un errore imprevisto. Il nostro team è stato notificato.' }}
      </p>

      <!-- Error Ref -->
      <div v-if="error.errorRef" class="bg-slate-100 dark:bg-slate-700/50 rounded-lg p-3">
        <p class="text-xs text-slate-500 dark:text-slate-400 mb-1">Codice errore (per il supporto):</p>
        <div class="flex items-center gap-2">
          <code class="flex-1 font-mono text-sm font-bold text-slate-800 dark:text-slate-200">
            {{ error.errorRef }}
          </code>
          <button
            @click="copyErrorRef"
            class="p-1.5 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            :aria-label="copied ? 'Copiato' : 'Copia codice errore'"
          >
            <Check v-if="copied" class="w-4 h-4 text-green-500" />
            <Copy v-else class="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex flex-col gap-2 pt-2">
        <button
          @click="reportProblem"
          class="w-full py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
        >
          <MessageSquarePlus class="w-4 h-4" />
          Segnala Problema
        </button>
        <div class="flex gap-2">
          <button
            @click="retry"
            class="flex-1 py-2.5 px-4 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw class="w-4 h-4" />
            Riprova
          </button>
          <button
            @click="goHome"
            class="flex-1 py-2.5 px-4 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Home class="w-4 h-4" />
            Home
          </button>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
